import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { zodParseWithDetailedLog } from "src/api/apiErrorLogs";
import { PostSchema } from "./postSchema";
import { isQueryReady } from "src/api/axiosClient/utils";
import { OlapScoresQuery } from "src/context/OlapScores/useOlapScoresParams";
import axios from "axios";

export const getProductPostKey = "getProductPost" as const;

type Options = {
    productId: number | undefined;
    companyId?: number;
    campaignId?: number;
    scoresQuery?: OlapScoresQuery;
    enabled?: boolean;
};

export const useGetProductPost = (options: Options) => {
    const { productId, companyId, campaignId, scoresQuery, enabled = true } = options;
    const isEnabled = enabled && productId !== undefined && companyId !== undefined && campaignId !== undefined;

    const query = useQuery({
        queryKey: [getProductPostKey, productId, companyId, campaignId, scoresQuery],
        cacheTime: 0,
        keepPreviousData: false,
        enabled: isEnabled,
        queryFn: async () => {
            if (!productId) {
                return;
            }

            const url = `/api/products/${productId}/ssq-post`;

            try {
                return await loggedInApiCall.get(url, {
                    headers: {
                        Accept: "application/json",
                    },
                    params: {
                        campaign: campaignId ?? undefined,
                        organization_id: companyId ?? undefined,
                        ...scoresQuery,
                    },
                });
            } catch (error) {
                if (axios.isAxiosError(error) && error.response?.status === 404) {
                    return null;
                }
                throw error;
            }
        },
        select: (response) => {
            if (!response) {
                return;
            }

            const post = response.data;
            const data = zodParseWithDetailedLog(post, PostSchema, {
                operation: "GET",
                endpoint: "/products/:id/ssq-post",
            });
            return data;
        },
    });

    const isReady = isQueryReady(query, isEnabled);

    return {
        post: query.data,
        isReady,
        ...query,
    };
};
